import { Code } from '@connectrpc/connect';
import {
  CloseTicketRequest,
  CreateSupportMessageRequest,
  CreateTicketRequest,
  GetTicketRequest,
  GetTicketsRequest,
  ListSupportInfosRequest,
  RateSupportMessageRequest,
} from 'gportal-grpc/gportal/api/support/v1/requests_pb';
import { SupportAddonType } from 'gportal-grpc/gportal/api/support/v1/support_addons_pb';
import { LocalizedError } from 'gportal-grpc/gportal/type/v1/localized_error_pb';
import useGrpcSupport from '~/composables/useGrpcSupport';
import { useAuthStore } from '~/store/auth';
import {
  mapProtoAddonPricing,
  mapProtoCategory,
  mapProtoTicket,
  mapProtoUserServer,
  toProtoRating,
  type MessageRating,
} from '~/store/support/protoHelpers';
import type {
  AddonPricing,
  AddonType,
  CloseTicketStatus,
  CreateTicketStatus,
  Ticket,
  TicketAttachmentUpload,
  TicketCategory,
  UserServer,
} from '~/store/support/types';

function extractTranslationKey(
  details?: Array<{ type: string; value: Uint8Array }>,
): string | null {
  if (!details) return null;

  const localizedDetail = details.find(
    (d) => d.type === 'gportal.type.v1.LocalizedError',
  );
  if (!localizedDetail) return null;

  try {
    const localized = LocalizedError.fromBinary(localizedDetail.value);
    return localized.translationKey || null;
  } catch {
    return null;
  }
}

// API functions --------------------------------------------------------------

export async function fetchTicketsApi(): Promise<Ticket[] | null> {
  try {
    const $grpcSupport = useGrpcSupport();
    const authStore = useAuthStore();
    const meta = authStore.getAuthorizationMetadata();

    const { response, error } = await $grpcSupport.executeApiCallActive(
      'getTickets',
      new GetTicketsRequest(),
      meta,
    );

    if (error) {
      console.error('Error fetching tickets:', error);
      return null;
    }

    if (!response?.tickets) return [];

    return response.tickets.map(mapProtoTicket);
  } catch (e) {
    console.error('Unexpected error fetching tickets:', e);
    return null;
  }
}

export async function fetchTicketByIdApi(
  ticketId: string,
): Promise<Ticket | null> {
  try {
    const $grpcSupport = useGrpcSupport();
    const req = new GetTicketRequest({ ticketId });
    const authStore = useAuthStore();
    const meta = authStore.getAuthorizationMetadata();

    const { response, error } = await $grpcSupport.executeApiCallActive(
      'getTicket',
      req,
      meta,
    );

    if (error) {
      console.error('Error fetching ticket details:', error);
      return null;
    }

    if (!response?.ticket) return null;

    return mapProtoTicket(response.ticket);
  } catch (e) {
    console.error('Unexpected error fetching ticket:', e);
    return null;
  }
}

export async function sendReplyApi(
  ticketId: string,
  body: string,
  attachments: TicketAttachmentUpload[] = [],
): Promise<{
  status: 'success' | 'error';
  ticket?: Ticket;
}> {
  try {
    const $grpcSupport = useGrpcSupport();
    const req = new CreateSupportMessageRequest({
      ticketId,
      body,
      attachments: attachments.map((a) => ({
        name: a.name,
        contentType: a.contentType,
        data: a.data,
      })),
    });
    const authStore = useAuthStore();
    const meta = authStore.getAuthorizationMetadata();

    const { response, error } = await $grpcSupport.executeApiCallActive(
      'createSupportMessage',
      req,
      meta,
    );

    if (error) {
      console.error('Error sending reply:', error);
      return { status: 'error' };
    }

    if (!response?.ticket) return { status: 'error' };

    return { status: 'success', ticket: mapProtoTicket(response.ticket) };
  } catch (e) {
    console.error('Unexpected error sending reply:', e);
    return { status: 'error' };
  }
}

export async function createTicketApi(
  title: string,
  body: string,
  categoryId?: string,
  gameserverId?: string,
  addonType?: AddonType,
): Promise<{
  status: CreateTicketStatus;
  ticket?: Ticket;
}> {
  try {
    const $grpcSupport = useGrpcSupport();

    // Map our internal addon type to proto enum
    let protoAddonType: SupportAddonType | undefined;
    if (addonType === 'priority') {
      protoAddonType = SupportAddonType.PRIORITY_SUPPORT;
    } else if (addonType === 'premium') {
      protoAddonType = SupportAddonType.PREMIUM_SUPPORT;
    }

    const req = new CreateTicketRequest({
      title,
      body,
      ...(categoryId ? { supportCategoryId: categoryId } : {}),
      ...(gameserverId ? { gameserverId } : {}),
      ...(protoAddonType !== undefined ? { addon: protoAddonType } : {}),
    });

    const authStore = useAuthStore();
    const meta = authStore.getAuthorizationMetadata();
    const { response, error } = await $grpcSupport.executeApiCallActive(
      'createTicket',
      req,
      meta,
    );

    if (error) {
      const translationKey = extractTranslationKey(error.details);

      const keyToStatus: Record<string, CreateTicketStatus> = {
        'grpc/no_saved_payment_method': 'no-payment-method',
        'grpc/payment_authorization_failed': 'payment-authorization-failed',
        'grpc/payment_void_failed': 'payment-void-failed',
      };

      if (translationKey && keyToStatus[translationKey]) {
        return { status: keyToStatus[translationKey] };
      }

      if (error.code === Code.FailedPrecondition) {
        return { status: 'no-payment-method' };
      }
      if (error.code === Code.InvalidArgument) {
        return { status: 'invalid-argument' };
      }
      return { status: 'error' };
    }

    if (!response?.ticket) return { status: 'error' };

    return { status: 'success', ticket: mapProtoTicket(response.ticket) };
  } catch (e) {
    console.error('Unexpected error creating ticket:', e);
    return { status: 'error' };
  }
}

export async function rateSupportMessageApi(
  messageId: string,
  rating: MessageRating,
): Promise<{ status: 'success' | 'already-rated' | 'error' }> {
  const numericRating = toProtoRating(rating);

  try {
    const $grpcSupport = useGrpcSupport();
    const req = new RateSupportMessageRequest({
      messageId,
      rating: numericRating,
    });

    const authStore = useAuthStore();
    const meta = authStore.getAuthorizationMetadata();
    const { response, error } = await $grpcSupport.executeApiCallActive(
      'rateSupportMessage',
      req,
      meta,
    );

    if (error) {
      console.error('Error rating support message:', error);
      return { status: 'error' };
    }

    // Check response status (SUCCESSFUL = 2, ALREADY_RATED = 3, FAILED = 1)
    if (response?.status === 3) {
      return { status: 'already-rated' };
    }

    if (response?.status !== 2) {
      console.error('Rating failed, status:', response?.status);
      return { status: 'error' };
    }

    return { status: 'success' };
  } catch (e) {
    console.error('Unexpected error rating support message:', e);
    return { status: 'error' };
  }
}

export async function closeTicketApi(ticketId: string): Promise<{
  status: CloseTicketStatus;
  ticket?: Ticket;
}> {
  try {
    const $grpcSupport = useGrpcSupport();
    const req = new CloseTicketRequest({
      ticketIdentifier: {
        case: 'ticketId',
        value: ticketId,
      },
    });
    const authStore = useAuthStore();
    const meta = authStore.getAuthorizationMetadata();

    const { response, error } = await $grpcSupport.executeApiCallActive(
      'closeTicket',
      req,
      meta,
    );

    if (error) {
      const translationKey = extractTranslationKey(error.details);

      if (translationKey === 'grpc/payment_void_failed') {
        return { status: 'payment-void-failed' };
      }
      return { status: 'error' };
    }

    if (!response?.ticket) return { status: 'error' };

    return { status: 'success', ticket: mapProtoTicket(response.ticket) };
  } catch (e) {
    console.error('Unexpected error closing ticket:', e);
    return { status: 'error' };
  }
}

export async function fetchSupportInfosApi(): Promise<{
  categories: TicketCategory[];
  userServers: UserServer[];
  addonPricing: AddonPricing[];
  hasPaymentMethod: boolean;
  paymentMethodUrl: string;
} | null> {
  try {
    const $grpcSupport = useGrpcSupport();
    const authStore = useAuthStore();
    const meta = authStore.getAuthorizationMetadata();

    const { response, error } = await $grpcSupport.executeApiCallActive(
      'listSupportInfos',
      new ListSupportInfosRequest(),
      meta,
    );

    if (error) {
      console.error('Error fetching support infos:', error);
      return null;
    }

    const categories = response?.categories
      ? response.categories.map(mapProtoCategory)
      : [];

    const userServers = response?.userServers
      ? response.userServers.map(mapProtoUserServer)
      : [];

    const addonPricing = response?.addons
      ? response.addons.map(mapProtoAddonPricing)
      : [];

    const hasPaymentMethod = !!response?.hasPaymentMethod;
    const paymentMethodUrl = response?.paymentMethodManagementUrl || '';

    return {
      categories,
      userServers,
      addonPricing,
      hasPaymentMethod,
      paymentMethodUrl,
    };
  } catch (e) {
    console.error('Unexpected error fetching support infos:', e);
    return null;
  }
}
